import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { useEffectiveUser } from '../contexts/AuthContext';
import PatchNotes from '../components/PatchNotes';
import { motion } from 'framer-motion';
import { 
  getDoc,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import Header from '../components/Header';
import { DevModeContext } from '../App';
import InstallPWA from '../components/InstallPWA';
import UpgradeToAccessModal from '../components/UpgradeToAccessModal';
import { FaCrown } from 'react-icons/fa';

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useEffectiveUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const isDevMode = useContext(DevModeContext);

  useEffect(() => {
    let unsubscribe = null;

    const setupUserListener = async () => {
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        
        unsubscribe = onSnapshot(userRef, (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            setIsAdmin(userData.isAdmin || false);
            setIsPremium(userData.membershipLevel === 'premium' || userData.membershipLevel === 'promo');
          }
        }, (error) => {
          console.error('Error in user listener:', error);
        });
      }
    };

    setupUserListener();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Check for modal flag in location state (from PremiumRoute redirect)
  useEffect(() => {
    if (location.state?.showUpgradeModal) {
      setShowUpgradeModal(true);
      // Clear the state to prevent modal showing on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const handleCommunityClick = () => {
    if (!isPremium && !isAdmin) {
      setShowUpgradeModal(true);
    } else {
      navigate('/community');
    }
  };

  const regularActions = [
    { title: 'Monthly/Lifetime Expenses', icon: '💰', path: '/expense', gradient: 'from-rose-400 to-pink-600' },
    { title: 'Recurring Payments', icon: '🔄', path: '/recurring', gradient: 'from-cyan-400 to-blue-600' },
    { title: 'Budgets', icon: '📊', path: '/budgets', gradient: 'from-emerald-400 to-green-600' },
    { title: 'Income Tracking', icon: '📈', path: '/income', gradient: 'from-purple-400 to-fuchsia-600' },
    { title: 'Statement', icon: '📝', path: '/statement', gradient: 'from-orange-400 to-red-500' },
    { title: 'Tutorial', icon: '📺', path: '/tutorial', gradient: 'from-lime-400 to-green-600' },
  ];

  const premiumActions = [
    // { title: 'Community Forum', icon: '💬', onClick: handleCommunityClick, gradient: 'from-yellow-500 to-amber-600', isPremium: true },
    { title: 'IntelliSpend',
      icon: '🔮', 
      path: '/intelliSpend', 
      gradient: 'from-indigo-400 to-violet-600',
      isPremium: true
    },
    { 
      title: 'Community Forum', 
      icon: '💬', 
      path: '/community', 
      gradient: 'from-yellow-400 to-amber-600', 
      isPremium: true, 
      // disabled: true 
    },
    {
      title: 'Business Inventory',
      icon: '📦',
      path: '/business-inventory',
      gradient: 'from-teal-400 to-cyan-600',
      isPremium: true
    },
  ];

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white select-none">
      <Header />

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6">
          {/* Quick Actions Grid */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="order-1 lg:order-2 lg:col-span-2 space-y-8"
          >
            {/* Regular Actions Section */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Your Dashboard</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {regularActions.map((action, index) => (
                  <motion.button
                    key={action.title}
                    custom={index}
                    initial="hidden"
                    animate="visible"
                    whileHover={!action.disabled ? { scale: 1.05 } : {}}
                    whileTap={!action.disabled ? { scale: 0.95 } : {}}
                    variants={cardVariants}
                    onClick={action.onClick || (() => !action.disabled && navigate(action.path))}
                    className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${action.gradient} p-6 shadow-lg transition-all duration-300 ${
                      action.disabled ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-2xl'
                    }`}
                  >
                    <div className={`absolute inset-0 bg-white/10 backdrop-blur-sm transition-opacity duration-300 ${
                      action.disabled ? 'opacity-50' : 'opacity-0 hover:opacity-100'
                    }`} />
                    <div className="relative z-10 flex flex-col items-center justify-center space-y-2">
                      <span className="text-3xl transform transition-transform duration-300 hover:scale-110">{action.icon}</span>
                      <span className="font-medium text-white text-sm md:text-base">{action.title}</span>
                      {action.disabled && (
                        <span className="text-xs text-white/80">Coming Soon</span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Premium Actions Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Premium Features</h2>
                {!isPremium && !isAdmin && (
                  <div className="flex items-center space-x-2 text-yellow-500">
                    <FaCrown className="h-5 w-5" />
                    <span className="text-sm font-medium">Upgrade to Access</span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {premiumActions.map((action, index) => (
                  <motion.button
                    key={action.title}
                    custom={index}
                    initial="hidden"
                    animate="visible"
                    whileHover={!action.disabled ? { scale: 1.05 } : {}}
                    whileTap={!action.disabled ? { scale: 0.95 } : {}}
                    variants={cardVariants}
                    onClick={() => {
                      if (!action.disabled) {
                        if (action.isPremium && !isPremium && !isAdmin) {
                          setShowUpgradeModal(true);
                        } else {
                          navigate(action.path);
                        }
                      }
                    }}
                    className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${action.gradient} p-6 shadow-lg transition-all duration-300 ${
                      action.disabled ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-2xl'
                    }`}
                  >
                    <div className={`absolute inset-0 bg-white/10 backdrop-blur-sm transition-opacity duration-300 ${
                      action.disabled ? 'opacity-50' : 'opacity-0 hover:opacity-100'
                    }`} />
                    <div className="relative z-10 flex flex-col items-center justify-center space-y-2">
                      <span className="text-3xl transform transition-transform duration-300 hover:scale-110">{action.icon}</span>
                      <span className="font-medium text-white text-sm md:text-base">{action.title}</span>
                      {action.disabled && (
                        <span className="text-xs text-white/80">Coming Soon</span>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Patch Notes Section - Now appears second on mobile with adjusted height */}
          <motion.div 
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1 lg:col-span-1 backdrop-blur-lg bg-gray-200 dark:bg-white/10 rounded-xl p-4 h-[calc(50vh)] lg:h-[calc(100vh-12rem)] overflow-hidden"
          >
            <div className="h-full">
              <PatchNotes isAdmin={isAdmin} />
            </div>
          </motion.div>
        </div>
      </main>
      
      <InstallPWA />
      <UpgradeToAccessModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
}

export default Dashboard;